import React, { useEffect, useRef, useState } from "react"
import Chevron from "../Chevron"
import { motion, AnimatePresence } from "framer-motion"
import useFixedDropdown from "../../hooks/useFixedDropdown"
import { useTranslation } from "react-i18next"

const PAGE_SIZE = 50

const MultiSelect = ({ id, openId, setOpenId, label, options, value, onChange }) => {
  const { t } = useTranslation()
  const open = openId === id
  const selected = value || []
  const { triggerRef, dropRef, pos } = useFixedDropdown(open, setOpenId)

  const [search, setSearch] = useState("")
  const [limit, setLimit] = useState(PAGE_SIZE)
  const searchRef = useRef(null)

  useEffect(() => {
    if (open) {
      setSearch("")
      setLimit(PAGE_SIZE)

      const timeout = setTimeout(() => {
        searchRef.current?.focus()
      }, 30)

      return () => clearTimeout(timeout)
    }
  }, [open])

  const toggle = (v) =>
    selected.includes(v) ? onChange(selected.filter((x) => x !== v)) : onChange([...selected, v])

  const filtered = search.trim()
    ? options.filter((o) => o.label?.toLowerCase().includes(search.trim().toLowerCase()))
    : options

  const visible = filtered.slice(0, limit)
  const hasMore = filtered.length > visible.length

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpenId(open ? null : id)}
        className={`w-full flex items-center justify-between gap-1 px-2.5 py-2 text-xs bg-white border rounded-xl transition-all focus:outline-none min-w-0 ${
          open ? "border-blue-400 ring-2 ring-blue-500/20" : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <span className="truncate text-left min-w-0">
          {selected.length === 0 ? (
            label
          ) : selected.length === 1 ? (
            options.find((o) => o.value === selected[0])?.label
          ) : (
            <>
              {selected.length} {t("admin.selected_short")}
            </>
          )}
        </span>

        <span className="flex-shrink-0">
          <Chevron open={open} />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={dropRef}
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.1, ease: "easeOut" }}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: Math.max(pos.width, 220),
              zIndex: 9999,
            }}
            className="bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Поиск */}
            <div className="p-1.5 border-b border-gray-100 flex-shrink-0">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setLimit(PAGE_SIZE)
                }}
                placeholder={t("admin.search")}
                className="w-full px-2.5 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
            </div>

            {/* Количество выбранных + сброс */}
            {selected.length > 0 && (
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50/60 flex-shrink-0">
                <span className="text-[11px] text-blue-600 font-medium">
                  {selected.length} {t("admin.selected_lower")}
                </span>

                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-[11px] text-gray-400 hover:text-red-500 transition-colors"
                >
                  {t("admin.reset")}
                </button>
              </div>
            )}

            {/* Список */}
            <div className="max-h-56 overflow-y-auto overscroll-contain">
              {visible.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5 flex-shrink-0"
                  />

                  <span className="text-xs text-gray-700 truncate min-w-0">{opt.label}</span>
                </label>
              ))}

              {filtered.length === 0 && (
                <div className="px-3 py-4 text-xs text-gray-400 text-center">
                  {t("admin.no_options")}
                </div>
              )}

              {hasMore && (
                <button
                  type="button"
                  onClick={() => setLimit((l) => l + PAGE_SIZE)}
                  className="w-full px-3 py-2 text-xs text-blue-600 hover:bg-blue-50/60 transition-colors border-t border-gray-100"
                >
                  {t("admin.show_more_with_count", {
                    count: filtered.length - visible.length,
                  })}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default MultiSelect
